/**
 * 支払キュー(2026-10-23・Gate 支出側 段階2)。docs/specs/2026-10-全体設計.md 4章。
 *
 * 経費申請・繰り返し支払い・取引先の請求書・外部システムの確定(Routine など)を、1つの一覧に並べて
 * 「いつまでに・いくら払うか」を見えるようにする。**見るだけの一覧**で、承認・支払いの操作は今までの画面で行う
 * (今の経費申請の流れ・状態の遷移は変えない)。
 *
 *  - 対象: 支出(type='expense')のうち、まだ支払っていないもの
 *      承認前 = 申請中・承認待ち / 支払い待ち = 承認済み・現金の受領待ち・受領済み
 *  - 期日: 取引の支払期日(due_date)→ 外部システムの取引で取引先に支払条件があれば、計上日から計算 → 申請時の支払日(payment_date)
 *  - 期日の区分: 期日超過 / 今日 / 7日以内 / 今月 / それ以降(今日を基準・JST)
 *
 * 具体例は tests/unit/payQueue.test.ts
 */
import { addDays, toMonth } from '@core/lib/date';
import type { DateStr, Transaction, TransactionStatus, Vendor, Yen } from '@core/lib/types';
import { dueDateFor } from '@core/partners/terms';

export const BEFORE_APPROVAL: TransactionStatus[] = ['applied', 'manager_checked'];
export const AWAITING_PAYMENT: TransactionStatus[] = ['approved', 'cash_ready', 'received'];
/** 読み込む状態(Firestore の 'in' 条件用) */
export const QUEUE_STATUSES: TransactionStatus[] = [...BEFORE_APPROVAL, ...AWAITING_PAYMENT];

export type QueueOrigin = 'expense' | 'invoice' | 'recurring' | 'external';

export const ORIGIN_LABELS: Record<QueueOrigin, string> = {
  expense: '経費申請',
  invoice: '取引先の請求書',
  recurring: '繰り返し支払い',
  external: '外部システム',
};

export type DueBucket = 'overdue' | 'today' | 'week' | 'month' | 'later';

export const BUCKET_LABELS: Record<DueBucket, string> = {
  overdue: '期日超過',
  today: '今日',
  week: '7日以内',
  month: '今月',
  later: 'それ以降',
};

export type QueueRow = {
  transaction: Transaction;
  origin: QueueOrigin;
  /** 承認前か(承認前は支払いの合計に入れない) */
  beforeApproval: boolean;
  dueDate: DateStr;
  /** 期日の出どころ。terms = 取引先の支払条件から計算 */
  dueFrom: 'due_date' | 'terms' | 'payment_date';
  bucket: DueBucket;
};

export function originOf(t: Pick<Transaction, 'source' | 'doc_type' | 'external'>): QueueOrigin {
  if (t.source === 'recurring') return 'recurring';
  // 外部システムの確定(Routine・Call Bank)。発生元の印(external)があるものも外部
  if (t.source === 'routine' || t.source === 'callbank' || t.external) return 'external';
  if (t.doc_type === 'invoice') return 'invoice';
  return 'expense';
}

export function bucketOf(due: DateStr, today: DateStr): DueBucket {
  if (due < today) return 'overdue';
  if (due === today) return 'today';
  if (due <= addDays(today, 6)) return 'week';
  if (toMonth(due) === toMonth(today)) return 'month';
  return 'later';
}

function dueOf(t: Transaction, vendor: Vendor | undefined): { dueDate: DateStr; dueFrom: QueueRow['dueFrom'] } {
  if (t.due_date) return { dueDate: t.due_date, dueFrom: 'due_date' };
  if (originOf(t) === 'external' && vendor?.pay_terms) {
    return { dueDate: dueDateFor(t.posting_date, vendor.pay_terms).dueDate, dueFrom: 'terms' };
  }
  return { dueDate: t.payment_date, dueFrom: 'payment_date' };
}

/** 一覧の行。期日の早い順(同じ日は金額の大きい順) */
export function buildPayQueue(transactions: Transaction[], vendors: Vendor[], today: DateStr): QueueRow[] {
  const vendorById = new Map(vendors.map((v) => [v.id, v]));
  return transactions
    .filter((t) => t.type === 'expense' && QUEUE_STATUSES.includes(t.status))
    .map((t): QueueRow => {
      const { dueDate, dueFrom } = dueOf(t, t.vendor_id ? vendorById.get(t.vendor_id) : undefined);
      return {
        transaction: t,
        origin: originOf(t),
        beforeApproval: BEFORE_APPROVAL.includes(t.status),
        dueDate,
        dueFrom,
        bucket: bucketOf(dueDate, today),
      };
    })
    .sort((a, b) => (a.dueDate === b.dueDate ? b.transaction.amount - a.transaction.amount : a.dueDate < b.dueDate ? -1 : 1));
}

export type QueueSummary = Record<DueBucket, { count: number; amount: Yen }> & {
  beforeApproval: { count: number; amount: Yen };
};

/** 期日の区分ごとの件数・金額。支払い待ち(承認済み以降)だけを数え、承認前は別に数える */
export function summarizeQueue(rows: QueueRow[]): QueueSummary {
  const empty = () => ({ count: 0, amount: 0 });
  const s: QueueSummary = {
    overdue: empty(),
    today: empty(),
    week: empty(),
    month: empty(),
    later: empty(),
    beforeApproval: empty(),
  };
  for (const r of rows) {
    const slot = r.beforeApproval ? s.beforeApproval : s[r.bucket];
    slot.count++;
    slot.amount += r.transaction.amount;
  }
  return s;
}
